import React, { useEffect, useState } from 'react'
import {
  BlockingLoader,
  Button,
  Container,
  ListContainer,
  State,
  TextBody,
  TextTitle,
  ToastContext,
  Tools
} from '@pmi/pmi-iqos-ds-react'
import {
  parseTranslations,
  PublishedComponent,
  LayoutContainer,
  CommonTranslations,
  htmlTranslation,
  cookie,
  analytics
} from '@pmi/pmi-iqos-shared-react'

import { IqosphereApiManager } from '@iqosphere-common/services'
import NoticeTemplateHelper from './notice-template-helper.js'
import ProfileNoticesTranslations from './profile-notices.json'

const ProfileNoticesComponent = ({ translations, line }) => {
  const pageSize = 5
  const [pageIndex, setPageIndex] = useState(0)
  const [notices, setNotices] = useState([])
  const [unreadNoticesAmount, setUnreadNoticesAmount] = useState(0)
  const [hasLoadedAll, setHasLoadedAll] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [noticeLoading, setNoticeLoading] = useState(null)
  const [regStatus, setRegStatus] = useState(null)

  const popupToast = ToastContext.getContextActions().popupToast

  const api = new IqosphereApiManager(line)

  useEffect(() => {
    loadData()
  }, [])

  const getUnreadNoticesAmount = () => {
    const loyaltyCookieContent = cookie.getLoyaltyProfile()
    return loyaltyCookieContent && loyaltyCookieContent.un
  }

  const updateNotice = id => {
    const newNotices = notices.map(notice => {
      if (notice.id === id) {
        return { ...notice, read: true }
      }
      return notice
    })
    const newUnreadNoticesAmount = getUnreadNoticesAmount()
    if (newUnreadNoticesAmount !== unreadNoticesAmount) {
      dispatchUnreadNoticesEvent(newUnreadNoticesAmount)
    }
    setUnreadNoticesAmount(newUnreadNoticesAmount)
    setNotices(newNotices)
  }

  const handleNotice = async id => {
    const notice = notices.find(notice => notice.id === id)

    if (skipUpdateNotice(notice)) return

    if (!notice.read && !noticeLoading) {
      setNoticeLoading(id)
      try {
        await api.iqosphereProfile.updateNotice(id)
        updateNotice(id)
      } catch (error) {
        handleLoadError()
      } finally {
        setNoticeLoading(null)
      }
    }
  }

  const handleLoadError = () => {
    popupToast({
      type: ToastContext.types.error,
      title: translations.tk_errorUpdatingNotice
    })
  }

  const dispatchUnreadNoticesEvent = newUnreadNoticesAmount => {
    window.dispatchEvent(
      new window.CustomEvent('unreadNoticesUpdate', {
        detail: { un: newUnreadNoticesAmount }
      })
    )
  }

  const loadData = async newPageIndex => {
    setIsLoading(true)
    try {
      const resNotices = await api.iqosphereProfile.getNotices(
        newPageIndex || pageIndex,
        pageSize
      )
      const newNotices = notices.concat(resNotices.notifications)
      const newUnreadNoticesAmount = getUnreadNoticesAmount()
      const profileInfo = cookie.getTokenInfo()

      setRegStatus(parseInt(profileInfo.reg_status))
      setNotices(newNotices)
      setHasLoadedAll(resNotices.notifications.length < pageSize)
      setUnreadNoticesAmount(newUnreadNoticesAmount)
      dispatchUnreadNoticesEvent(newUnreadNoticesAmount)
    } catch (error) {
      handleLoadError()
    } finally {
      setIsLoading(false)
    }
  }

  const handleLoadNotices = () => {
    analytics.clickEvent({
      eventCategory: 'My account',
      eventLabel: 'Notices - Load More'
    })
    const newPageIndex = pageIndex + 1
    setPageIndex(newPageIndex)
    loadData(newPageIndex)
  }

  const skipUpdateNotice = notice => {
    if (
      notice.template === 'prize/winning' ||
      notice.template === 'prize/toropical/winning' ||
      notice.template === 'prize/winning/cherish'
    ) {
      return true
    }
    return false
  }

  return (
    <Container position={Container.positions.relative}>
      {isLoading && <BlockingLoader loading />}
      <LayoutContainer xMainLayout={!Tools.isDesktop()}>
        <Container pt={['8', '0']}>
          <TextTitle heading={TextTitle.headings.h2}>
            {translations.tk_title}
          </TextTitle>
        </Container>
        <Container py="8">
          <TextBody
            size={TextBody.sizes.s}
            {...htmlTranslation(translations, 'tk_text')}
          />
        </Container>
      </LayoutContainer>
      <ListContainer columns={1}>
        {notices.map((notice, index) => {
          const contentNotice = NoticeTemplateHelper.getNoticeDisplay({
            template: notice.template,
            data: notice.data,
            date: notice.date,
            read: notice.read,
            handleNotice,
            id: notice.id,
            regState: regStatus
          })

          return contentNotice ? (
            <Container
              key={index}
              pt="4"
              pb="6"
              px="8"
              divider={Container.dividers.top}
              display={Container.displays.flex}
              bg={
                notice.read === false
                  ? Container.backgrounds.white
                  : Container.backgrounds.hardWhite
              }
              onClickHandler={
                skipUpdateNotice(notice)
                  ? null
                  : () => {
                      handleNotice(notice.id)
                    }
              }
              position={Container.positions.relative}>
              {noticeLoading === notice.id && <BlockingLoader loading />}
              <Container pr="4">
                <State
                  state={
                    notice.read === false
                      ? State.states.warning
                      : State.states.validGreen
                  }
                  size={State.sizes.xs}
                />
              </Container>
              <Container>{contentNotice}</Container>
            </Container>
          ) : null
        })}
      </ListContainer>
      {hasLoadedAll ? null : (
        <Container
          py="8"
          display={Container.displays.flex}
          justifyContent={Container.justifyContentValues.center}>
          <Button
            disabled={isLoading}
            variant={Button.variants.secondary}
            text={translations.tk_loadMoreNotices}
            onClickHandler={handleLoadNotices}
          />
        </Container>
      )}
    </Container>
  )
}

const ProfileNotices = props => {
  return (
    <ToastContext>
      <PublishedComponent
        innerComponent={ProfileNoticesComponent}
        {...ComponentDefaultProps}
        {...props}
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...ProfileNoticesTranslations,
  ...CommonTranslations
}

const ComponentDefaultProps = {
  componentName: 'ProfileNotices',
  translations: {
    ...parseTranslations(ComponentTranslations, true)
  }
}

ProfileNotices.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    line: !!req?.query?.line,
    componentName: ComponentDefaultProps.componentName,
    defaultTranslations: ComponentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    getSpecificInitialProps: () => {}
  })
}

export default ProfileNotices
